import { Network, ChevronDown, ChevronRight, Box, GitBranch, Zap, ArrowRight, Download } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';

interface PartsProcessOutlineDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  showToast?: (message: string) => void;
  addToResources?: (resource: any) => void;
  showHeader?: boolean;
}

export default function PartsProcessOutlineDisplay({
  result,
  theme = 'light',
  showToast,
  addToResources,
  showHeader = true
}: PartsProcessOutlineDisplayProps) {
  const [expandedParts, setExpandedParts] = useState<Set<string>>(new Set());
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [expandedFlows, setExpandedFlows] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'parts' | 'steps' | 'flows'>('parts');

  // Check if this result contains parts/process outline data
  if (!result || typeof result !== 'object') return null;

  const isPartsProcessOutline = result.analysis_id !== undefined ||
                                 result.parts_count !== undefined ||
                                 result.full_result !== undefined;

  if (!isPartsProcessOutline) return null;

  // Get the full result data
  const fullResult = result.full_result || result;
  const partsHierarchy = fullResult.parts_hierarchy || [];
  const processSteps = fullResult.process_steps || [];
  const systemFlows = fullResult.system_flows || [];

  const togglePart = (partId: string) => {
    const newExpanded = new Set(expandedParts);
    if (newExpanded.has(partId)) {
      newExpanded.delete(partId);
    } else {
      newExpanded.add(partId);
    }
    setExpandedParts(newExpanded);
  };

  const toggleStep = (stepId: string) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(stepId)) {
      newExpanded.delete(stepId);
    } else {
      newExpanded.add(stepId);
    }
    setExpandedSteps(newExpanded);
  };

  const toggleFlow = (flowName: string) => {
    const newExpanded = new Set(expandedFlows);
    if (newExpanded.has(flowName)) {
      newExpanded.delete(flowName);
    } else {
      newExpanded.add(flowName);
    }
    setExpandedFlows(newExpanded);
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      hardware: 'bg-blue-100 text-blue-800 border-blue-200',
      software: 'bg-purple-100 text-purple-800 border-purple-200',
      sensor: 'bg-green-100 text-green-800 border-green-200',
      actuator: 'bg-orange-100 text-orange-800 border-orange-200',
      controller: 'bg-red-100 text-red-800 border-red-200',
      interface: 'bg-cyan-100 text-cyan-800 border-cyan-200',
      structure: 'bg-gray-100 text-gray-800 border-gray-200',
      mechanism: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    };
    return colors[category?.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getRelationshipColor = (relType: string) => {
    const types: Record<string, string> = {
      connects_to: 'text-blue-600',
      controls: 'text-red-600',
      powered_by: 'text-yellow-600',
      processes_data_from: 'text-purple-600',
      transmits_to: 'text-green-600',
      supports: 'text-gray-600',
      mounted_on: 'text-cyan-600',
      integrated_with: 'text-indigo-600',
    };
    return types[relType] || 'text-gray-600';
  };

  // Helper to get part name by ID
  const getPartName = (partId: string) => {
    const part = partsHierarchy.find((p: any) => p.part_id === partId);
    return part ? part.part_name : partId;
  };

  const exportToDocx = async () => {
    try {
      const children: Paragraph[] = [];

      // Title
      children.push(
        new Paragraph({
          text: "IR Parts & Process Outline",
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        })
      );

      // Parts Hierarchy Section
      if (partsHierarchy.length > 0) {
        children.push(
          new Paragraph({
            text: `Parts Hierarchy (${partsHierarchy.length} parts)`,
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          })
        );

        partsHierarchy.forEach((part: any, index: number) => {
          // Part header
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `${part.part_id}: `,
                  bold: true,
                }),
                new TextRun({
                  text: part.part_name,
                  bold: true,
                }),
              ],
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 200, after: 100 },
            })
          );

          // Category
          children.push(
            new Paragraph({
              text: `Category: ${part.category}`,
              italics: true,
              spacing: { after: 100 },
            })
          );

          // Description
          children.push(
            new Paragraph({
              text: part.description,
              spacing: { after: 100 },
            })
          );

          // Technical Details
          if (part.technical_details) {
            const td = part.technical_details;
            if (td.configuration) {
              children.push(
                new Paragraph({
                  text: `Configuration: ${td.configuration}`,
                  indent: { left: 360 },
                  spacing: { after: 50 },
                })
              );
            }
            if (td.materials && td.materials.length > 0) {
              children.push(
                new Paragraph({
                  text: `Materials: ${td.materials.join(', ')}`,
                  indent: { left: 360 },
                  spacing: { after: 50 },
                })
              );
            }
            if (td.specifications && td.specifications.length > 0) {
              children.push(
                new Paragraph({
                  text: `Specifications: ${td.specifications.join(', ')}`,
                  indent: { left: 360 },
                  spacing: { after: 50 },
                })
              );
            }
          }

          // Relationships
          if (part.relationships && part.relationships.length > 0) {
            children.push(
              new Paragraph({
                text: "Relationships:",
                bold: true,
                spacing: { before: 100, after: 50 },
                indent: { left: 360 },
              })
            );
            part.relationships.forEach((rel: any) => {
              children.push(
                new Paragraph({
                  text: `• ${rel.relationship_type.replace(/_/g, ' ')} → ${rel.related_part_id}: ${getPartName(rel.related_part_id)} - ${rel.description}`,
                  spacing: { after: 50 },
                  indent: { left: 720 },
                })
              );
            });
          }
        });
      }

      // System Flows Section
      if (systemFlows.length > 0) {
        children.push(
          new Paragraph({
            text: `System Flows (${systemFlows.length} flows)`,
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          })
        );

        systemFlows.forEach((flow: any) => {
          children.push(
            new Paragraph({
              text: flow.flow_name,
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 200, after: 100 },
            })
          );

          children.push(
            new Paragraph({
              text: `Type: ${flow.flow_type}`,
              italics: true,
              spacing: { after: 50 },
            })
          );

          children.push(
            new Paragraph({
              text: flow.description,
              spacing: { after: 100 },
            })
          );

          if (flow.path && flow.path.length > 0) {
            children.push(
              new Paragraph({
                text: "Flow Path:",
                bold: true,
                spacing: { before: 100, after: 50 },
              })
            );

            flow.path.sort((a: any, b: any) => a.sequence - b.sequence).forEach((pathStep: any) => {
              children.push(
                new Paragraph({
                  text: `${pathStep.sequence}. ${pathStep.part_id}: ${getPartName(pathStep.part_id)} - ${pathStep.action}`,
                  spacing: { after: 50 },
                  indent: { left: 360 },
                })
              );
            });
          }
        });
      }

      // Process Steps Section
      if (processSteps.length > 0) {
        children.push(
          new Paragraph({
            text: `Process Steps (${processSteps.length} steps)`,
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          })
        );

        processSteps.sort((a: any, b: any) => a.step_number - b.step_number).forEach((step: any) => {
          // Step header
          children.push(
            new Paragraph({
              text: `Step ${step.step_number}: ${step.step_name}`,
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 200, after: 100 },
            })
          );

          // Description
          children.push(
            new Paragraph({
              text: step.description,
              spacing: { after: 100 },
            })
          );

          // Parts involved
          if (step.parts_involved && step.parts_involved.length > 0) {
            children.push(
              new Paragraph({
                text: `Parts Involved: ${step.parts_involved.map((id: string) => `${id}: ${getPartName(id)}`).join(', ')}`,
                spacing: { after: 50 },
              })
            );
          }

          // Inputs
          if (step.inputs && step.inputs.length > 0) {
            children.push(
              new Paragraph({
                text: "Inputs:",
                bold: true,
                spacing: { before: 100, after: 50 },
              })
            );
            step.inputs.forEach((input: string) => {
              children.push(
                new Paragraph({
                  text: `• ${input}`,
                  spacing: { after: 50 },
                  indent: { left: 360 },
                })
              );
            });
          }

          // Outputs
          if (step.outputs && step.outputs.length > 0) {
            children.push(
              new Paragraph({
                text: "Outputs:",
                bold: true,
                spacing: { before: 100, after: 50 },
              })
            );
            step.outputs.forEach((output: string) => {
              children.push(
                new Paragraph({
                  text: `• ${output}`,
                  spacing: { after: 50 },
                  indent: { left: 360 },
                })
              );
            });
          }

          // Sub-steps
          if (step.sub_steps && step.sub_steps.length > 0) {
            children.push(
              new Paragraph({
                text: "Sub-steps:",
                bold: true,
                spacing: { before: 100, after: 50 },
              })
            );
            step.sub_steps.forEach((subStep: any) => {
              children.push(
                new Paragraph({
                  text: `${subStep.sub_step_number} ${subStep.description}`,
                  spacing: { after: 50 },
                  indent: { left: 360 },
                })
              );
            });
          }
        });
      }

      if (partsHierarchy.length === 0 && processSteps.length === 0 && systemFlows.length === 0) {
        children.push(
          new Paragraph({
            text: "No parts or processes generated in outline.",
            italics: true,
            spacing: { before: 200 },
          })
        );
      }

      // Create Word document
      const doc = new Document({
        sections: [{
          properties: {},
          children: children,
        }],
      });

      // Generate and download the document
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'parts-process-outline.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (showToast) {
        showToast('Parts & Process Outline exported to Word document');
      }
    } catch (error) {
      console.error('Error exporting Parts & Process Outline to DOCX:', error);
      if (showToast) {
        showToast('Error exporting Parts & Process Outline');
      }
    }
  };

  // Render individual part with hierarchy
  const renderPart = (part: any, level: number = 0) => {
    const isExpanded = expandedParts.has(part.part_id);
    const hasSubparts = part.subparts && part.subparts.length > 0;
    const relationships = part.relationships || [];
    const techDetails = part.technical_details || {};

    return (
      <div key={part.part_id} className={`${level > 0 ? 'ml-6 border-l-2 border-gray-200 pl-4' : ''}`}>
        <Card className="mb-3">
          <CardHeader
            className="cursor-pointer hover:bg-gray-50 transition-colors py-3"
            onClick={() => togglePart(part.part_id)}
          >
            <div className="flex items-start gap-3">
              <div className="mt-1">
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Box className="w-4 h-4 text-blue-600" />
                  <h4 className="text-sm font-semibold">{part.part_name}</h4>
                  <Badge variant="outline" className={`text-xs ${getCategoryColor(part.category)}`}>
                    {part.category}
                  </Badge>
                  {hasSubparts && (
                    <Badge variant="outline" className="text-xs">
                      {part.subparts.length} subpart{part.subparts.length !== 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-gray-600">{part.description}</p>
              </div>
            </div>
          </CardHeader>

          {isExpanded && (
            <CardContent className="pt-0 pb-3">
              {/* Technical Details */}
              {(techDetails.materials || techDetails.specifications || techDetails.configuration) && (
                <div className="mb-3 p-3 bg-gray-50 rounded border border-gray-200">
                  <h5 className="text-xs font-semibold text-gray-700 mb-2">Technical Details</h5>
                  {techDetails.materials && techDetails.materials.length > 0 && (
                    <div className="mb-1">
                      <span className="text-xs text-gray-600">Materials: </span>
                      <span className="text-xs text-gray-800">{techDetails.materials.join(', ')}</span>
                    </div>
                  )}
                  {techDetails.specifications && techDetails.specifications.length > 0 && (
                    <div className="mb-1">
                      <span className="text-xs text-gray-600">Specifications: </span>
                      <span className="text-xs text-gray-800">{techDetails.specifications.join('; ')}</span>
                    </div>
                  )}
                  {techDetails.configuration && (
                    <div>
                      <span className="text-xs text-gray-600">Configuration: </span>
                      <span className="text-xs text-gray-800">{techDetails.configuration}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Relationships */}
              {relationships.length > 0 && (
                <div className="mb-3">
                  <h5 className="text-xs font-semibold text-gray-700 mb-2">Relationships</h5>
                  <div className="space-y-1">
                    {relationships.map((rel: any, idx: number) => (
                      <div key={idx} className="flex items-start gap-2 text-xs">
                        <ArrowRight className={`w-3 h-3 mt-0.5 flex-shrink-0 ${getRelationshipColor(rel.relationship_type)}`} />
                        <div>
                          <span className={`font-medium ${getRelationshipColor(rel.relationship_type)}`}>
                            {rel.relationship_type.replace(/_/g, ' ')}
                          </span>
                          <span className="text-gray-600"> → {rel.related_part_id}: {getPartName(rel.related_part_id)}</span>
                          {rel.description && (
                            <p className="text-gray-600 mt-0.5">{rel.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Render subparts recursively */}
        {isExpanded && hasSubparts && (
          <div className="mt-2">
            {part.subparts.map((subpartId: string) => {
              const subpart = partsHierarchy.find((p: any) => p.part_id === subpartId);
              return subpart ? renderPart(subpart, level + 1) : null;
            })}
          </div>
        )}
      </div>
    );
  };

  // Get top-level parts (no parent)
  const topLevelParts = partsHierarchy.filter((p: any) => !p.parent_part_id);

  return (
    <div className="space-y-4">
      {showHeader && (
        <div className="border-b pb-2 mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Parts & Process Outline</h3>
          <button
            onClick={exportToDocx}
            className="text-sm px-3 py-1 rounded flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white"
            title="Export outline as Word document"
          >
            <Download className="w-4 h-4" />
            Export DOCX
          </button>
        </div>
      )}

      {/* Summary Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Network className="w-5 h-5 text-blue-600" />
            Outline Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-600">{partsHierarchy.length}</div>
              <div className="text-xs text-gray-600 mt-1">Parts</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-indigo-600">{processSteps.length}</div>
              <div className="text-xs text-gray-600 mt-1">Process Steps</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600">{systemFlows.length}</div>
              <div className="text-xs text-gray-600 mt-1">System Flows</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('parts')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            activeTab === 'parts'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Parts Hierarchy ({partsHierarchy.length})
        </button>
        <button
          onClick={() => setActiveTab('steps')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            activeTab === 'steps'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Process Steps ({processSteps.length})
        </button>
        <button
          onClick={() => setActiveTab('flows')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            activeTab === 'flows'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          System Flows ({systemFlows.length})
        </button>
      </div>

      {/* Parts Hierarchy Tab */}
      {activeTab === 'parts' && (
        <div>
          {topLevelParts.length > 0 ? (
            topLevelParts.map((part: any) => renderPart(part, 0))
          ) : (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                <Box className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>No parts identified in outline.</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Process Steps Tab */}
      {activeTab === 'steps' && (
        <div>
          {processSteps.length > 0 ? (
            processSteps.sort((a: any, b: any) => a.step_number - b.step_number).map((step: any) => {
              const isExpanded = expandedSteps.has(step.step_id);
              return (
                <Card key={step.step_id} className="mb-3">
                  <CardHeader
                    className="cursor-pointer hover:bg-gray-50 transition-colors py-3"
                    onClick={() => toggleStep(step.step_id)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className="text-xs bg-blue-600">Step {step.step_number}</Badge>
                          <h4 className="text-sm font-semibold">{step.step_name}</h4>
                        </div>
                        <p className="text-xs text-gray-600">{step.description}</p>
                      </div>
                    </div>
                  </CardHeader>

                  {isExpanded && (
                    <CardContent className="pt-0 pb-3 space-y-3">
                      {step.parts_involved && step.parts_involved.length > 0 && (
                        <div>
                          <h5 className="text-xs font-semibold text-gray-700 mb-1">Parts Involved</h5>
                          <div className="flex flex-wrap gap-1">
                            {step.parts_involved.map((partId: string, idx: number) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {partId}: {getPartName(partId)}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {step.inputs && step.inputs.length > 0 && (
                        <div>
                          <h5 className="text-xs font-semibold text-gray-700 mb-1">Inputs</h5>
                          <ul className="list-disc list-inside text-xs text-gray-600">
                            {step.inputs.map((input: string, idx: number) => (
                              <li key={idx}>{input}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {step.outputs && step.outputs.length > 0 && (
                        <div>
                          <h5 className="text-xs font-semibold text-gray-700 mb-1">Outputs</h5>
                          <ul className="list-disc list-inside text-xs text-gray-600">
                            {step.outputs.map((output: string, idx: number) => (
                              <li key={idx}>{output}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {step.sub_steps && step.sub_steps.length > 0 && (
                        <div>
                          <h5 className="text-xs font-semibold text-gray-700 mb-1">Sub-steps</h5>
                          <ol className="list-decimal list-inside text-xs text-gray-600 space-y-1">
                            {step.sub_steps.map((subStep: any, idx: number) => (
                              <li key={idx}>
                                <span className="font-medium">{subStep.sub_step_number}</span> {subStep.description}
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
              );
            })
          ) : (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                <GitBranch className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>No process steps identified in outline.</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* System Flows Tab */}
      {activeTab === 'flows' && (
        <div>
          {systemFlows.length > 0 ? (
            systemFlows.map((flow: any, flowIdx: number) => {
              const isExpanded = expandedFlows.has(flow.flow_name);
              return (
                <Card key={flowIdx} className="mb-3">
                  <CardHeader
                    className="cursor-pointer hover:bg-gray-50 transition-colors py-3"
                    onClick={() => toggleFlow(flow.flow_name)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Zap className="w-4 h-4 text-yellow-600" />
                          <h4 className="text-sm font-semibold">{flow.flow_name}</h4>
                          <Badge variant="outline" className="text-xs">
                            {flow.flow_type}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-600">{flow.description}</p>
                      </div>
                    </div>
                  </CardHeader>

                  {isExpanded && flow.path && flow.path.length > 0 && (
                    <CardContent className="pt-0 pb-3">
                      <h5 className="text-xs font-semibold text-gray-700 mb-2">Flow Path</h5>
                      <div className="space-y-2">
                        {flow.path.sort((a: any, b: any) => a.sequence - b.sequence).map((pathStep: any, idx: number) => (
                          <div key={idx} className="flex items-start gap-2">
                            <Badge variant="outline" className="text-xs flex-shrink-0">
                              {pathStep.sequence}
                            </Badge>
                            <div className="flex-1">
                              <div className="text-xs font-medium text-gray-800">{pathStep.part_id}: {getPartName(pathStep.part_id)}</div>
                              <div className="text-xs text-gray-600">{pathStep.action}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })
          ) : (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                <Zap className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>No system flows identified in outline.</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
